/**
 * Grouped sidebar with icons.
 *
 * The menu is still server-driven (we fetch /me/menu) but we don't render
 * it raw — we group it into Setup / Operations / Insights / Advanced based
 * on the screenKey prefix. This gives a clean, predictable layout that
 * matches the mental model of the platform.
 *
 * If the server returns extra menu items we don't recognize, they fall
 * into "Other" at the bottom — so nothing is lost, just better organized.
 */

import { useQuery } from "@tanstack/react-query";
import { signOut } from "firebase/auth";
import { useEffect, useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";

import { api } from "../api/client";
import { auth } from "../firebase.config";
import {
  BadgeIcon,
  BarChartIcon,
  BookIcon,
  BuildingIcon,
  CheckSquareIcon,
  ClipboardIcon,
  EyeIcon,
  FileIcon,
  FolderIcon,
  HomeIcon,
  LinkIcon,
  LogOutIcon,
  MenuIcon,
  RadioIcon,
  SettingsIcon,
  ShieldIcon,
  SparkIcon,
  StarIcon,
  TrendingIcon,
  UsersIcon,
} from "./icons";

export interface MenuNode {
  id: string;
  label: string;
  screenKey?: string;
  icon?: string;
  order?: number;
  children: MenuNode[];
}

interface NavEntry {
  to: string;
  label: string;
  icon: ReactNode;
  badgeKey?: string;  // points at /me/notifications counts.<key>
}

interface NotificationCounts {
  counts: Record<string, number>;
}

// Map screenKey → icon. Anything not in the map gets a folder icon.
const ICON_MAP: Record<string, ReactNode> = {
  "admin/hierarchy": <BuildingIcon />,
  "admin/users": <UsersIcon />,
  "admin/roles": <ShieldIcon />,
  "admin/positions": <BadgeIcon />,
  "admin/races": <StarIcon />,
  "admin/polling-stations": <BuildingIcon />,
  "admin/assignments": <LinkIcon />,
  "admin/screens": <SettingsIcon />,
  "admin/menu": <MenuIcon />,
  "tasks": <CheckSquareIcon />,
  "intel": <RadioIcon />,
  "cases": <FileIcon />,
  "poi": <EyeIcon />,
  "performance": <TrendingIcon />,
  "popularity": <StarIcon />,
  "reports": <BarChartIcon />,
  "audit": <ClipboardIcon />,
};

interface Group {
  label: string;
  keys: string[];        // ordered screen keys we expect to belong here
}

// Reports comes first — viewing data is the most common arrival path.
// Operations sits below it as a capture-only zone (each entry takes the
// user straight to the wizard for that record kind).
const GROUPS: Group[] = [
  {
    label: "Reports",
    keys: ["reports", "map", "threats", "intel", "poi", "performance", "tasks", "voters", "audit"],
  },
  {
    label: "Operations",
    keys: ["op/intel", "op/poi", "op/task", "op/performance", "op/voters"],
  },
  {
    label: "Setup",
    keys: [
      "admin/hierarchy",
      "admin/polling-stations",
      "admin/races",
      "admin/users",
      "admin/roles",
      "admin/positions",
      "admin/people",
      "admin/organogram",
    ],
  },
  {
    label: "Advanced",
    keys: ["admin/screens", "admin/menu"],
  },
];

// Synthetic "Operations" entries — these aren't returned by the server menu
// (they don't correspond to a screen) but click straight through to the
// matching wizard. Reusing the same routes the lists already render means we
// don't need a separate page per capture; the existing wizard takes over.
interface SyntheticEntry { key: string; label: string; to: string; icon: ReactNode; }
const OPERATIONS_SYNTHETIC: SyntheticEntry[] = [
  { key: "op/intel",       label: "Capture Intel",           to: "/app/intel?capture=1", icon: <RadioIcon /> },
  { key: "op/poi",         label: "Capture POI",             to: "/app/poi/new",         icon: <EyeIcon /> },
  { key: "op/task",        label: "Assign Task",             to: "/app/tasks/new",       icon: <CheckSquareIcon /> },
  { key: "op/performance", label: "Capture Performance",     to: "/app/performance/new", icon: <TrendingIcon /> },
  { key: "op/voters",      label: "Capture Voters",          to: "/app/voters/new",      icon: <UsersIcon /> },
];

const HIDE_KEYS = new Set(["admin/assignments", "cases", "popularity"]);

// Reports group keeps the screen-key names; rename labels so they read as
// listing/aggregating entry points rather than capture entry points.
const LABEL_OVERRIDES: Record<string, string> = {
  "intel": "Intel feed",
  "tasks": "Tasks",
  "performance": "Performance reports",
  "popularity": "Popularity reports",
  "cases": "Cases",
  "poi": "POI watchlist",
  "reports": "Dashboards",
  "audit": "Audit log",
};

// Maps a screenKey → the notifications.counts.<key> that drives its badge.
const BADGE_KEY: Record<string, string> = {
  "tasks": "tasks",
  "intel": "intel",
  "cases": "cases",
  "poi": "poi",
  "performance": "performance",
  "popularity": "popularity",
};

function flatten(nodes: MenuNode[]): NavEntry[] {
  const out: NavEntry[] = [];
  function walk(n: MenuNode) {
    if (n.screenKey) {
      out.push({
        to: `/app/${n.screenKey}`,
        label: LABEL_OVERRIDES[n.screenKey] ?? n.label,
        icon: ICON_MAP[n.screenKey] ?? <FolderIcon />,
        badgeKey: BADGE_KEY[n.screenKey],
      });
    }
    n.children.forEach(walk);
  }
  nodes.forEach(walk);
  return out;
}

export function Sidebar({
  menu,
  email,
  isSuperadmin,
}: {
  menu: MenuNode[];
  email: string | null | undefined;
  isSuperadmin: boolean;
}) {
  // Poll notifications every 60s. Errors are tolerated silently — a missing
  // badge isn't a fatal UX failure.
  const notifications = useQuery<NotificationCounts>({
    queryKey: ["notifications"],
    queryFn: () => api<NotificationCounts>("/me/notifications"),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: 0,
  });
  const counts = notifications.data?.counts ?? {};

  const all = flatten(menu).filter((e) => !HIDE_KEYS.has(e.to.replace(/^\/app\//, "")));
  const byKey = new Map<string, NavEntry>();
  for (const e of all) {
    const key = e.to.replace(/^\/app\//, "");
    byKey.set(key, e);
  }
  // Pre-load the synthetic Operations entries into the lookup. These don't
  // come from the server menu — the user gets the option to capture even if
  // the server hasn't seeded a separate menu item.
  for (const e of OPERATIONS_SYNTHETIC) {
    byKey.set(e.key, { to: e.to, label: e.label, icon: e.icon });
  }
  // Synthetic entries for People + Organogram (Setup group) — only show for
  // superadmins.
  if (isSuperadmin) {
    byKey.set("admin/people", { to: "/app/admin/people", label: "People", icon: <UsersIcon /> });
    byKey.set("admin/organogram", { to: "/app/admin/organogram", label: "Organogram", icon: <SparkIcon /> });
    // Races + Polling stations — surfaced to superadmins until the seed pushes
    // them into /me/menu for everyone with the matching permission.
    byKey.set("admin/races", { to: "/app/admin/races", label: "Races", icon: <StarIcon /> });
    byKey.set("admin/polling-stations", { to: "/app/admin/polling-stations", label: "Polling stations", icon: <BuildingIcon /> });
    byKey.set("voters", { to: "/app/voters", label: "Voters", icon: <UsersIcon /> });
  }
  // Map is in the Reports group; available to anyone who can already see lists.
  byKey.set("map", { to: "/app/map", label: "Map", icon: <BuildingIcon /> });
  byKey.set("threats", { to: "/app/threats", label: "Threat Detection", icon: <ShieldIcon /> });

  const grouped = GROUPS.map((g) => ({
    label: g.label,
    items: g.keys.map((k) => byKey.get(k)).filter(Boolean) as NavEntry[],
  })).filter((g) => g.items.length > 0);

  // Anything the server returned that we didn't bucket above (and isn't a
  // synthetic operations entry) goes to "Other".
  const groupedKeys = new Set(GROUPS.flatMap((g) => g.keys));
  const other = all.filter((e) => !groupedKeys.has(e.to.replace(/^\/app\//, "")));
  if (other.length > 0) grouped.push({ label: "Other", items: other });

  return (
    <aside className="sidebar">
      <div className="brand">
        <span className="brand-dot" aria-hidden />
        Campaign Intelligence
      </div>

      <nav>
        <NavLink to="/app" end className={navClass}>
          <HomeIcon className="icon" /> Home
        </NavLink>

        {grouped.map((g) => (
          <CollapsibleGroup key={g.label} label={g.label}>
            {g.items.map((it) => {
              const count = it.badgeKey ? counts[it.badgeKey] ?? 0 : 0;
              return (
                <NavLink key={it.to} to={it.to} className={navClass}>
                  <span className="icon" aria-hidden>{it.icon}</span>
                  <span style={{ flex: 1 }}>{it.label}</span>
                  {count > 0 && <NavBadge n={count} />}
                </NavLink>
              );
            })}
          </CollapsibleGroup>
        ))}

        {isSuperadmin && (
          <div className="group">
            <div className="group-header">Help</div>
            <NavLink to="/app/admin/guide" className={navClass}>
              <span className="icon" aria-hidden><BookIcon /></span>
              <span style={{ flex: 1 }}>Superadmin Guide</span>
            </NavLink>
          </div>
        )}
      </nav>

      <div className="user">
        <div className="user-email">{email}</div>
        <div className="user-role">{isSuperadmin ? "Superadmin" : "Member"}</div>
        <button
          onClick={async () => {
            await signOut(auth);
            window.location.replace("/");
          }}
        >
          <LogOutIcon style={{ width: 14, height: 14, marginRight: 6, verticalAlign: -2 }} />
          Sign out
        </button>
      </div>
    </aside>
  );
}

function navClass({ isActive }: { isActive: boolean }) {
  return `nav-item${isActive ? " active" : ""}`;
}

function CollapsibleGroup({ label, children }: { label: string; children: ReactNode }) {
  const storageKey = `sidebar:group:${label}`;
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v === null ? true : v === "1";
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem(storageKey, open ? "1" : "0"); } catch { /* ignore */ }
  }, [storageKey, open]);
  return (
    <div className="group">
      <button
        type="button"
        className="group-header"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{ background: "transparent", border: "none", textAlign: "left", width: "100%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--muted)" }}
      >
        <span>{label}</span>
        <span aria-hidden style={{ fontSize: 10, transform: open ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 120ms" }}>▾</span>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

function NavBadge({ n }: { n: number }) {
  return (
    <span
      aria-label={`${n} new`}
      style={{
        background: "var(--accent)",
        color: "#fff",
        fontSize: 11,
        fontWeight: 600,
        padding: "1px 7px",
        borderRadius: 999,
        minWidth: 18,
        textAlign: "center",
        lineHeight: "16px",
      }}
    >
      {n > 99 ? "99+" : n}
    </span>
  );
}

export const SparkPlaceholder = SparkIcon; // re-export for completeness
